import type {
  useFormatDate,
  useFormatTime,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type {
  CashListTransaction,
  CashListTransactionType,
} from '@pflegenavi/frontend/api-nursing-home';
import type { LabelColor } from '@pflegenavi/web-components';
import type { CashTransactionTableRowModel } from '../interfaces/CashTransactionTableRowModel';
import {
  TIME_FORMAT,
  YEAR_MONTH_DAY_SHORT_FORMAT,
} from '@pflegenavi/shared/constants';
import type { Theme } from '@mui/material/styles';

interface formatCashTransactionTableRowModelOpts {
  fDate: ReturnType<typeof useFormatDate>;
  fCurrencyCents: ReturnType<typeof useFormatting>['fCurrencyCents'];
  fTime: ReturnType<typeof useFormatTime>;
  theme: Theme;
  t: (key: string) => string;
}

const TYPE_COLORS: Record<CashListTransactionType, LabelColor> = {
  deposit: 'success',
  withdrawal: 'error',
  adjustment: 'info',
  stripe_transfer: 'default',
};

export const formatCashTransactionTableRowModel = (
  row: CashListTransaction,
  opts: formatCashTransactionTableRowModelOpts
): CashTransactionTableRowModel => {
  const date = new Date(row.inserted_at);
  const employeeString =
    `${row.employee?.first_name ?? ''} ${
      row.employee?.last_name ?? ''
    }`.trim() || '--';

  return {
    id: row.id,
    type: row.type,
    typeLabelString: opts.t(`cashManagement.table-row.${row.type}`),
    typeLabelColor: TYPE_COLORS[row.type],
    residents: row.related_residents.map((resident) => ({
      id: resident.id,
      name: `${resident.firstname ?? ''} ${resident.lastname ?? ''}`.trim(),
    })),
    dateString: opts.fDate(date, YEAR_MONTH_DAY_SHORT_FORMAT),
    timeString: opts.fTime(date, TIME_FORMAT),
    employeeString,
    amountString: opts.fCurrencyCents(row.amount),
    amountColor:
      row.amount < 0
        ? 'error.main'
        : row.amount > 0
        ? 'success.main'
        : opts.theme.palette.grey['400'],
    separateFeeAmountInCents: row.separate_fee_amount,
    separateFeeAmountString: opts.fCurrencyCents(row.separate_fee_amount),
    separateFeeAmountColor:
      row.separate_fee_amount !== 0
        ? 'warning.main'
        : opts.theme.palette.grey['400'],
    immediateFeeAmountInCents: row.immediate_fee_amount,
    immediateFeeAmountString: opts.fCurrencyCents(row.immediate_fee_amount),
    immediateFeeAmountColor: opts.theme.palette.grey['400'],
    balanceAfterString: opts.fCurrencyCents(row.balance_after),
    balanceBeforeString: opts.fCurrencyCents(row.balance_before),
    notes: row.notes || '--',
    coinChange: row.coin_change ?? [],
    bankAccountAmount: row.bank_account_amount_change,
    groupId: row.group_id,
  };
};
